// Lightweight client info collector for attaching request headers
// Exposes getClientHeaders(): Promise<Record<string, string>>

let memoizedHeaders = null;
const STORAGE_KEY = 'unio_client_headers_v1';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

async function fetchWithTimeout(url, options = {}, timeoutMs = 3000) {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);

    fetch(url, { ...options, signal: controller.signal })
      .then((res) => resolve(res))
      .catch((err) => reject(err))
      .finally(() => clearTimeout(id));
  });
}

async function getPublicIp() {
  // Try a couple of common endpoints with CORS enabled
  const candidates = [
    'https://api.ipify.org?format=json',
    'https://api64.ipify.org?format=json',
  ];

  for (const url of candidates) {
    try {
      const res = await fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, 3000);
      if (!res.ok) continue;
      const data = await res.json();
      if (data && typeof data.ip === 'string' && data.ip.length > 0) {
        return data.ip;
      }
    } catch (_) {
      // ignore and try next
    }
  }
  return 'unknown';
}

async function getLocationInfo() {
  // Try to get country info from IP geolocation services
  const candidates = [
    'https://ipapi.co/json/',
    'https://ipwhois.app/json/',
  ];

  for (const url of candidates) {
    try {
      const res = await fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, 3000);
      console.log('Location fetch response:', res);
      if (!res.ok) continue;
      const data = await res.json();
      
      // Different APIs have different response formats
      const country = data.country_name || data.country || null;
      const countryCode = data.country_code || data.country_code2 || null;
      const languages = data.languages || null;
      const currency = data.currency || data.currency_code || null;
      
      if (country && countryCode) {
        return { 
          country, 
          countryCode: countryCode.toUpperCase(),
          language: languages ? (Array.isArray(languages) ? languages[0] : languages) : null,
          currency: currency ? (typeof currency === 'string' ? currency : currency.code || null) : null
        };
      }
    } catch (_) {
      // ignore and try next
    }
  }
  
  // Fallback: try to infer from locale/timezone
  try {
    const locale = navigator.language || navigator.languages?.[0];
    if (locale && locale.includes('-')) {
      const countryCode = locale.split('-')[1].toUpperCase();
      return { country: null, countryCode, language: null, currency: null };
    }
  } catch (_) {
    // ignore
  }
  
  return { country: null, countryCode: null, language: null, currency: null };
}

function safeGet(fn, fallback = 'unknown') {
  try {
    const v = fn();
    if (v === undefined || v === null || v === '') return fallback;
    return v;
  } catch (_) {
    return fallback;
  }
}

function collectStaticInfo() {
  const userAgent = safeGet(() => navigator.userAgent, 'unknown');
  const platform = safeGet(() => navigator.platform, 'unknown');
  const language = safeGet(() => navigator.language || navigator.languages?.[0], 'unknown');
  const timezone = safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone, 'unknown');
  const screenRes = safeGet(() => `${window.screen.width}x${window.screen.height}`, 'unknown');
  const referrer = safeGet(() => document.referrer, '');

  return { userAgent, platform, language, timezone, screenRes, referrer };
}

export async function getClientHeaders() {
  if (memoizedHeaders) return memoizedHeaders;

  // Attempt session/local cache first to avoid repeated lookups
  try {
    const fromStorage = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (fromStorage) {
      const cached = JSON.parse(fromStorage);
      if (cached && typeof cached === 'object' && cached.headers && cached.timestamp) {
        const age = Date.now() - cached.timestamp;
        // If cache is still valid (less than 1 hour old), use it
        if (age < CACHE_DURATION_MS) {
          memoizedHeaders = cached.headers;
          return memoizedHeaders;
        }
        // Cache expired, will fetch fresh data below
      }
    }
  } catch (_) {
    // ignore storage issues
  }

  const staticInfo = collectStaticInfo();
  const ip = await getPublicIp();
  const locationInfo = await getLocationInfo();

  const headers = {
    // 'X-Client-IP': ip,
   // 'X-User-Agent': staticInfo.userAgent,
    'X-Client-Platform': staticInfo.platform,
    'X-Client-Timezone': staticInfo.timezone,
    'X-Client-Locale': staticInfo.language,
    'X-Client-Screen': staticInfo.screenRes,
  };

  // Add country and country code if available
  if (locationInfo.country) {
    headers['X-Client-Country'] = locationInfo.country;
  }
  if (locationInfo.countryCode) {
    headers['X-Client-Country-Code'] = locationInfo.countryCode;
  }
  if (locationInfo.language) {
    headers['X-Client-Language'] = locationInfo.language;
  }
  if (locationInfo.currency) {
    headers['X-Client-Currency'] = locationInfo.currency;
  }

  memoizedHeaders = headers;
  
  // Store with timestamp for cache expiration
  const cacheData = {
    headers,
    timestamp: Date.now()
  };
  
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
  } catch (_) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
    } catch (_) {
      // ignore
    }
  }

  return headers;
}

export { getLocationInfo };
export default { getClientHeaders };
